/**
 * THE FEATURE CLOUD: every feature the room has recorded, sized by how many
 * products ship it. What the market treats as table stakes stands out; what
 * one product alone offers sits small. The same reading as the feature matrix
 * (`reports.build_feature_cloud`), at a glance. A word opens its feature.
 */
import { useContext, useState } from 'react';
import type { BoardroomFeatureCloud, BoardroomFeatureCloudWord } from '../../../types';
import { TableFilterContext, filterWords, textMatches } from './tableFilter';
import FeatureTwinsPanel, { type TwinsApi } from './FeatureTwinsPanel';

const MIN_PX = 11;
const MAX_PX = 30;

/** How big a word is drawn: by the square root of how many ship it, so the
 *  one feature everybody has does not dwarf the rest into dots. Pure. */
export function wordSize(products: number, most: number): number {
  if (most <= 1) return MIN_PX + 2;
  const t = Math.sqrt(Math.max(0, products - 1) / (most - 1));
  return Math.round(MIN_PX + t * (MAX_PX - MIN_PX));
}

/** How strongly it reads, by the share of products that ship it. Pure. */
export function wordTone(share: number): string {
  if (share >= 0.5) return 'text-indigo-800 font-semibold dark:text-indigo-200';
  if (share >= 0.2) return 'text-indigo-600 dark:text-indigo-300';
  if (share >= 0.05) return 'text-gray-700 dark:text-gray-200';
  return 'text-gray-400 dark:text-gray-500';
}

export default function FeatureCloudReport({
  data,
  onOpen,
  twins,
}: {
  data: BoardroomFeatureCloud;
  onOpen: (itemId: number) => void;
  /** The duplicates panel's calls; absent where the page cannot merge. */
  twins?: TwinsApi;
}) {
  const [order, setOrder] = useState<'most' | 'az'>('most');
  // THE CLOUD OR ITS DUPLICATES: the long tail is mostly one feature written
  // several ways, and thinning it is done from here, a group at a time.
  const [tidying, setTidying] = useState(false);
  const words = filterWords(useContext(TableFilterContext));
  const shown: BoardroomFeatureCloudWord[] = data.rows
    .filter(w => textMatches(`${w.title} ${w.examples.join(' ')}`, words))
    .slice()
    .sort(order === 'az' ? (a, b) => a.title.localeCompare(b.title) : (a, b) => b.products - a.products);
  const most = Math.max(1, ...data.rows.map(w => w.products));
  const { products, features, shown: drawn } = data.totals;

  if (tidying && twins) {
    return (
      <div className="min-w-0" data-testid="feature-cloud">
        <button
          type="button"
          onClick={() => setTidying(false)}
          className="mb-2 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
          data-testid="feature-cloud-back"
        >
          ← back to the cloud
        </button>
        <FeatureTwinsPanel api={twins} onOpen={onOpen} />
      </div>
    );
  }
  if (!data.rows.length) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="feature-cloud">
        No features recorded yet{products ? ` on the ${products} products here` : ''}. Reading their pages fills this in.
      </p>
    );
  }
  return (
    <div className="min-w-0" data-testid="feature-cloud">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span data-testid="feature-cloud-totals">
          {features} feature{features === 1 ? '' : 's'} across {products} product{products === 1 ? '' : 's'}
          {drawn < features ? `, the ${drawn} most shipped drawn` : ''} · bigger means more products ship it; the small number is how many
        </span>
        {/* FEW READ, SO NO OVERLAP YET: say so, or a cloud of 1s reads as a
            market where nobody shares a feature. */}
        {data.totals.with_features !== undefined && data.totals.with_features * 2 < products && (
          <span className="basis-full text-amber-700 dark:text-amber-300" data-testid="feature-cloud-thin">
            Features recorded for {data.totals.with_features} of {products} products. Shared features show once more
            products are read.
          </span>
        )}
        <span className="ml-auto flex gap-1">
          {twins && (
            <button
              type="button"
              onClick={() => setTidying(true)}
              className="rounded border border-indigo-200 px-1.5 py-0.5 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/20"
              title="Every group of features that look like one feature written several ways, to merge in one go"
              data-testid="feature-cloud-tidy"
            >
              Merge duplicates
            </button>
          )}
          {(['most', 'az'] as const).map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setOrder(k)}
              className={`rounded px-1.5 py-0.5 ${
                order === k
                  ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid={`feature-cloud-order-${k}`}
            >
              {k === 'most' ? 'most shipped' : 'A to Z'}
            </button>
          ))}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 leading-tight">
        {shown.map(w => (
          <button
            key={w.id}
            type="button"
            onClick={() => onOpen(w.id)}
            style={{ fontSize: `${wordSize(w.products, most)}px` }}
            className={`hover:underline ${wordTone(w.share)}`}
            title={`Shipped by ${w.products} of ${products} products${w.examples.length ? `: ${w.examples.join(', ')}${w.products > w.examples.length ? ', …' : ''}` : ''}`}
            data-testid={`feature-word-${w.id}`}
          >
            {w.title}
            <span
              className="ml-0.5 align-super text-[11px] font-normal text-gray-400 dark:text-gray-500"
              data-testid={`feature-word-count-${w.id}`}
            >
              {w.products}
            </span>
          </button>
        ))}
      </div>
      {!shown.length && <p className="text-xs text-gray-400">Nothing here matches the filter.</p>}
    </div>
  );
}
